import * as React from 'react';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid2';
import {styled} from '@mui/system';
import {Trans, useTranslation} from "react-i18next";
import TextField from "@mui/material/TextField";
import {Typography} from "@mui/material";
import UserInfo from "./UserInfo";

export const FormGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

export default function DataForm({userInfo, validity, onChange, onBlur, withoutEmail, billing, readOnly, personType}) {
    const {t} = useTranslation();

    return (
        <Grid container spacing={2}>
            {withoutEmail ? '' : <FormGrid size={{xs: 12}}>
                <FormLabel htmlFor="email" required>
                    <Trans i18nKey="auth.email"/>
                </FormLabel>
                <TextField
                    aria-readonly={readOnly}
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('auth.email')}
                    autoComplete={`${billing ? 'billing' : 'shipping'} email`}
                    required
                    size="small"
                    value={userInfo.email || ''}
                    onChange={(event) => onChange('email', event.target.value)}
                    onBlur={(event) => onBlur('email')}
                    error={Boolean(validity.email)}
                    helperText={t(validity.email)}
                />
            </FormGrid>}
            <FormGrid size={{xs: 12}}>
                <FormLabel htmlFor="telNr" required>
                    <Trans i18nKey="addressForm.telNr"/>
                </FormLabel>
                <TextField
                    aria-readonly={readOnly}
                    id="telNr"
                    name="telNr"
                    type="tel"
                    placeholder={t('addressForm.telNr')}
                    autoComplete={`${billing ? 'billing' : 'shipping'} tel`}
                    required
                    size="small"
                    value={userInfo.telNr || ''}
                    onChange={(event) => onChange('telNr', event.target.value)}
                    onBlur={(event) => onBlur('telNr')}
                    error={Boolean(validity.telNr)}
                    helperText={t(validity.telNr)}
                />
            </FormGrid>
            <FormGrid size={{xs: 12, md: 6}}>
                <FormLabel htmlFor="first-name" required>
                    <Trans i18nKey="addressForm.firstName"/>
                </FormLabel>
                <TextField
                    id="first-name"
                    name="first-name"
                    type="name"
                    placeholder="John"
                    autoComplete={`${billing ? 'billing' : 'shipping'} given-name`}
                    required
                    size="small"
                    value={userInfo.firstName || ''}
                    onChange={(event) => onChange('firstName', event.target.value)}
                    onBlur={(event) => onBlur('firstName')}
                    error={Boolean(validity.firstName)}
                    helperText={t(validity.firstName)}
                />
            </FormGrid>
            <FormGrid size={{xs: 12, md: 6}}>
                <FormLabel htmlFor="last-name" required>
                    <Trans i18nKey="addressForm.lastName"/>
                </FormLabel>
                <TextField
                    id="last-name"
                    name="last-name"
                    type="last-name"
                    placeholder="Snow"
                    autoComplete={`${billing ? 'billing' : 'shipping'} family-name`}
                    required
                    size="small"
                    value={userInfo.lastName || ''}
                    onChange={(event) => onChange('lastName', event.target.value)}
                    onBlur={(event) => onBlur('lastName')}
                    error={Boolean(validity.lastName)}
                    helperText={t(validity.lastName)}
                />
            </FormGrid>
            {personType === UserInfo.types.JURIDICAL_PERSON &&
                <>
                    <FormGrid size={{xs: 12, md: 6}}>
                        <FormLabel htmlFor="firm-name" required>
                            <Trans i18nKey="addressForm.firmName"/>
                        </FormLabel>
                        <TextField
                            id="firm-name"
                            name="firm-name"
                            type="organization"
                            placeholder="Diwiprint SRL"
                            autoComplete={`${billing ? 'billing' : 'shipping'} organization`}
                            required
                            size="small"
                            value={userInfo.firmName || ''}
                            onChange={(event) => onChange('firmName', event.target.value)}
                            onBlur={(event) => onBlur('firmName')}
                            error={Boolean(validity.firmName)}
                            helperText={t(validity.firmName)}
                        />
                    </FormGrid>
                    <FormGrid size={{xs: 12, md: 6}}>
                        <FormLabel htmlFor="vatNr" required>
                            <Trans i18nKey="addressForm.vatNr"/>
                        </FormLabel>
                        <TextField
                            id="vatNr"
                            name="vatNr"
                            type="vatNr"
                            placeholder="51145626"
                            autoComplete={`${billing ? 'billing' : 'shipping'} vatNr`}
                            required
                            size="small"
                            value={userInfo.vatNr || ''}
                            onChange={(event) => onChange('vatNr', event.target.value)}
                            onBlur={(event) => onBlur('vatNr')}
                            error={Boolean(validity.vatNr)}
                            helperText={t(validity.vatNr)}
                        />
                    </FormGrid>
                </>
            }
            <FormGrid size={{xs: 12}}>
                <FormLabel htmlFor="address1" required>
                    <Trans i18nKey="addressForm.addLine1"/>
                </FormLabel>
                <TextField
                    id="address1"
                    name="address1"
                    type="address1"
                    placeholder={t('addressForm.addLine1PlaceHolder')}
                    autoComplete={`${billing ? 'billing' : 'shipping'} address-line1`}
                    required
                    size="small"
                    value={userInfo.addressLine1 || ''}
                    onChange={(event) => onChange('addressLine1', event.target.value)}
                    onBlur={(event) => onBlur('addressLine1')}
                    error={Boolean(validity.addressLine1)}
                    helperText={t(validity.addressLine1)}
                />
            </FormGrid>
            <FormGrid size={{xs: 12}}>
                <FormLabel htmlFor="address2"><Trans i18nKey="addressForm.addLine2"/></FormLabel>
                <TextField
                    id="address2"
                    name="address2"
                    type="address2"
                    placeholder={t('addressForm.addLine2PlaceHolder')}
                    autoComplete={`${billing ? 'billing' : 'shipping'} address-line2`}
                    size="small"
                    value={userInfo.addressLine2 || ''}
                    onChange={(event) => onChange('addressLine2', event.target.value)}
                    onBlur={(event) => onBlur('addressLine2')}
                    error={Boolean(validity.addressLine2)}
                    helperText={t(validity.addressLine2)}
                />
            </FormGrid>
            <FormGrid size={{xs: 6}}>
                <FormLabel htmlFor="city" required>
                    <Trans i18nKey="addressForm.city"/>
                </FormLabel>
                <TextField
                    id="city"
                    name="city"
                    type="city"
                    placeholder="Cluj-Napoca"
                    autoComplete={`${billing ? 'billing' : 'shipping'} locality`}
                    required
                    size="small"
                    value={userInfo.city || ''}
                    onChange={(event) => onChange('city', event.target.value)}
                    onBlur={(event) => onBlur('city')}
                    error={Boolean(validity.city)}
                    helperText={t(validity.city)}
                />
            </FormGrid>
            <FormGrid size={{xs: 6}}>
                <FormLabel htmlFor="state" required>
                    <Trans i18nKey="addressForm.county"/>
                </FormLabel>
                <TextField
                    id="state"
                    name="state"
                    type="state"
                    placeholder="Cluj"
                    autoComplete={`${billing ? 'billing' : 'shipping'} address-level1`}
                    required
                    size="small"
                    value={userInfo.county || ''}
                    onChange={(event) => onChange('county', event.target.value)}
                    onBlur={(event) => onBlur('county')}
                    error={Boolean(validity.county)}
                    helperText={t(validity.county)}
                />
            </FormGrid>
            <FormGrid size={{xs: 6}}>
                <FormLabel htmlFor="zip" required>
                    <Trans i18nKey="addressForm.zipCode"/>
                </FormLabel>
                <TextField
                    id="zip"
                    name="zip"
                    type="zip"
                    placeholder="400000"
                    autoComplete={`${billing ? 'billing' : 'shipping'} postal-code`}
                    required
                    size="small"
                    value={userInfo.zipCode || ''}
                    onChange={(event) => onChange('zipCode', event.target.value)}
                    onBlur={(event) => onBlur('zipCode')}
                    error={Boolean(validity.zipCode)}
                    helperText={t(validity.zipCode)}
                />
            </FormGrid>
            <FormGrid size={{xs: 6}}>
                <FormLabel htmlFor="country" required>
                    <Trans i18nKey="addressForm.country"/>
                </FormLabel>
                <Typography sx={{height: '40px', display: 'flex', alignItems: 'center'}}>
                    <Trans i18nKey="addressForm.countryname"/>
                </Typography>
            </FormGrid>
        </Grid>
    );
}
